import React, { Component, ErrorInfo, ReactNode } from 'react';
import { isChunkLoadError, handleChunkLoadError } from '../../utils/lazyWithRetry';

interface ChunkErrorBoundaryProps {
  children: ReactNode;
}

interface ChunkErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary specifically designed to handle chunk/module loading failures.
 *
 * This component catches errors that occur when dynamically imported modules
 * fail to load (common after new deployments when chunk hashes change).
 *
 * When a chunk loading error is detected:
 * 1. It attempts to reload the page to fetch the latest asset manifest
 * 2. After max retries, it displays a user-friendly error message with a reload button
 *
 * For non-chunk errors, the error is re-thrown to be handled by parent error boundaries.
 */
class ChunkErrorBoundary extends Component<
  ChunkErrorBoundaryProps,
  ChunkErrorBoundaryState
> {
  constructor(props: ChunkErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ChunkErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ChunkErrorBoundary] Error caught:', error);
    console.error('[ChunkErrorBoundary] Error info:', errorInfo);

    // If it's a chunk loading error, attempt a page reload
    if (isChunkLoadError(error)) {
      const reloading = handleChunkLoadError(error);
      if (reloading) {
        // Page is reloading, don't update state
        return;
      }
    }
  }

  handleReload = (): void => {
    // Clear any cached retry state before manual reload
    try {
      sessionStorage.removeItem('chunk_load_retry');
    } catch {
      // Ignore storage errors
    }
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      // Only show UI for chunk loading errors
      // Other errors should be handled by parent error boundaries
      if (isChunkLoadError(this.state.error)) {
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
              width: '100%',
              padding: '20px',
              textAlign: 'center',
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            }}
          >
            <div
              style={{
                maxWidth: '500px',
                padding: '40px',
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              }}
            >
              <div
                style={{
                  fontSize: '48px',
                  marginBottom: '20px',
                }}
              >
                🔄
              </div>
              <h1
                style={{
                  fontSize: '24px',
                  color: '#333',
                  marginBottom: '16px',
                }}
              >
                Update Available
              </h1>
              <p
                style={{
                  fontSize: '16px',
                  color: '#666',
                  marginBottom: '24px',
                  lineHeight: '1.5',
                }}
              >
                A new version of the application is available. Please reload the page
                to get the latest updates.
              </p>
              <button
                onClick={this.handleReload}
                style={{
                  padding: '12px 32px',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#fff',
                  backgroundColor: '#053f98',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#042d6e';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#053f98';
                }}
              >
                Reload Page
              </button>
              <p
                style={{
                  fontSize: '12px',
                  color: '#999',
                  marginTop: '20px',
                }}
              >
                If this problem persists, please try clearing your browser cache or
                contact support at{' '}
                <a href="mailto:support@ept.ai" style={{ color: '#053f98' }}>
                  support@ept.ai
                </a>
              </p>
            </div>
          </div>
        );
      }

      // For non-chunk errors, re-throw to be handled by other error boundaries
      throw this.state.error;
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
